import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, RotateCw } from "lucide-react";

export default function ProvablyFair() {
  const [seeds, setSeeds] = useState(null);
  const [newClient, setNewClient] = useState("");
  const [revealed, setRevealed] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/pf/seeds");
    setSeeds(data);
  };

  useEffect(() => {
    load();
  }, []);

  const rotate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pf/seeds/rotate", { new_client_seed: newClient || null });
      setRevealed(data);
      toast.success("Seeds rotated. Previous server seed revealed below.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied");
  };

  return (
    <div className="flex flex-col gap-6 fade-up max-w-4xl">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter flex items-center gap-3">
          <ShieldCheck className="text-emerald-400" size={32} /> Provably Fair
        </h1>
        <div className="text-zinc-500 text-sm mt-1">
          Every bet is derived from <code className="font-mono-num text-amber-400">HMAC_SHA256(server_seed, client_seed:nonce)</code>.
          We publish a hash of our server seed; you choose the client seed. Rotate to reveal the previous seed and verify any bet.
        </div>
      </div>

      <section className="panel p-6 font-mono-num text-sm">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Current Session</div>
        <div className="grid gap-3">
          <Row k="Server Seed Hash" v={seeds?.server_seed_hash} onCopy={copy} />
          <Row k="Client Seed" v={seeds?.client_seed} onCopy={copy} />
          <Row k="Nonce" v={String(seeds?.nonce ?? "—")} onCopy={copy} />
        </div>
      </section>

      <section className="panel p-6">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Rotate Seeds</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={newClient}
            onChange={(e) => setNewClient(e.target.value)}
            placeholder="New client seed (optional — random if blank)"
            className="panel flex-1 px-4 py-3 font-mono-num text-sm outline-none focus:border-amber-500/40"
            style={{ background: "#0F0F11" }}
            data-testid="pf-new-client-seed"
          />
          <button onClick={rotate} disabled={busy} className="btn-cyan rounded-xl px-5 py-3 flex items-center gap-2" data-testid="pf-rotate">
            <RotateCw size={14} /> Rotate
          </button>
        </div>
      </section>

      {revealed && (
        <section className="panel p-6 border-emerald-500/30">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-3">Previous Server Seed Revealed</div>
          <div className="grid gap-3">
            <Row k="Revealed Server Seed" v={revealed.revealed_server_seed} onCopy={copy} />
            <Row k="Its Hash (match current)" v={revealed.revealed_server_seed_hash} onCopy={copy} />
            <Row k="New Server Seed Hash" v={revealed.new_server_seed_hash} onCopy={copy} />
            <Row k="New Client Seed" v={revealed.new_client_seed} onCopy={copy} />
          </div>
        </section>
      )}

      <section className="panel p-6 text-sm text-zinc-400 space-y-3">
        <div className="font-display font-bold text-white text-lg">How to verify</div>
        <ol className="list-decimal list-inside space-y-1 marker:text-amber-400 marker:font-mono-num">
          <li>Rotate seeds to reveal the current server seed.</li>
          <li>For each past bet, take <code>server_seed</code>, your <code>client_seed</code>, and the bet's <code>nonce</code>.</li>
          <li>Compute <code>HMAC_SHA256(server_seed, "client_seed:nonce:0")</code>.</li>
          <li>Game-specific formula (see docs) deterministically derives the outcome from the HMAC output.</li>
          <li>The result must match the bet's stored outcome — or we're busted.</li>
        </ol>
      </section>
    </div>
  );
}

function Row({ k, v, onCopy }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-white/5 last:border-0">
      <span className="text-xs uppercase tracking-widest text-zinc-500 min-w-[180px]">{k}</span>
      <code className="flex-1 break-all text-zinc-300">{v || "—"}</code>
      {v && (
        <button onClick={() => onCopy(v)} className="text-xs text-amber-400 hover:text-amber-300 uppercase tracking-widest shrink-0">
          Copy
        </button>
      )}
    </div>
  );
}
